import React from 'react';
import { Box, Typography, Card, CardContent, List, ListItem, Chip, Paper } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import GroupIcon from '@mui/icons-material/Group';
import WebhookIcon from '@mui/icons-material/Webhook';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const primaryColor = '#4c43a3';

const CodeBlock = ({ code }) => (
    <Paper
        sx={{
            bgcolor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            fontFamily: '"Fira Mono", "Consolas", monospace',
            fontSize: '0.875rem',
            overflow: 'auto',
            whiteSpace: 'pre',
            color: '#000',
        }}
    >
        <code>{code}</code>
    </Paper>
);

const MethodChip = ({ method }) => {
    const colors = {
        GET: '#0d6efd',
        POST: '#198754',
        PUT: '#ffc107',
        DELETE: '#dc3545',
    };
    return (
        <Chip
            label={method}
            size="small"
            sx={{
                bgcolor: colors[method],
                color: 'white',
                fontWeight: 700,
                fontSize: '0.75rem',
                mr: 1,
            }}
        />
    );
};

const EndpointCard = ({
    id,
    method,
    endpoint,
    description,
    tag,
    requestExample = [],
    responseExample,
    importantParams = [],
}) => {
    // Normalize requestExample to array
    const requestExamples = Array.isArray(requestExample)
        ? requestExample
        : requestExample
        ? [{ title: 'Request Example:', content: requestExample }]
        : [];

    return (
        <Card
            id={id}
            sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MethodChip method={method} />
                    <Typography
                        component="code"
                        sx={{
                            bgcolor: '#f5f5f5',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            color: '#000',
                        }}
                    >
                        {endpoint}
                    </Typography>
                </Box>

                {/* Meta */}
                {tag && (
                    <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                        Tag: {tag}
                    </Typography>
                )}

                <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                    {description}
                </Typography>

                {/* Request Examples */}
                {requestExamples.map((example, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                            {example.title}
                        </Typography>
                        <CodeBlock code={example.content} />
                    </Box>
                ))}

                {/* Response Example */}
                {responseExample && (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                            Response Example{' '}
                            <Chip
                                label="status 200"
                                size="small"
                                sx={{
                                    bgcolor: '#d4edda',
                                    color: '#155724',
                                    fontSize: '0.7rem',
                                }}
                            />
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <CodeBlock code={responseExample} />
                        </Box>
                    </>
                )}

                {/* Important Parameters */}
                {importantParams.length > 0 && (
                    <Paper
                        sx={{
                            bgcolor: '#e8f4fd',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #b8daff',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#004085' }}>
                            Important Parameters:
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                            {importantParams.map((param, index) => (
                                <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                                    <Typography variant="body2" sx={{ color: '#004085' }}>
                                        {param}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}
            </CardContent>
        </Card>
    );
};

const ApiDocumentation = () => {
    const sidebarLinks = [
        {
            id: 'post-scorm-files',
            icon: <AddCircleIcon fontSize="small" />,
            title: 'Upload a Scorm Package',
            endpoint: 'POST /scorm-files/upload/',
        },
        {
            id: 'get-scorm-file-id',
            icon: <ArrowForwardIcon fontSize="small" />,
            title: 'Get a Scorm File by ID',
            endpoint: 'GET /scorm-files/{scorm_file_id}/',
        },
        {
            id: 'delete-scorm-file-id',
            icon: <DeleteIcon fontSize="small" />,
            title: 'Delete a Scorm File',
            endpoint: 'DELETE /scorm-files/{scorm_file_id}/',
        },
        {
            id: 'get-scorm-files',
            icon: <ArrowForwardIcon fontSize="small" />,
            title: 'List All Scorm Files',
            endpoint: 'GET /scorm-files/',
        },
        {
            id: 'post-content-extractions',
            icon: <AddCircleIcon fontSize="small" />,
            title: 'Start Content Extraction',
            endpoint: 'POST /content-extractions/',
        },
        {
            id: 'get-content-extractions-id',
            icon: <ArrowForwardIcon fontSize="small" />,
            title: 'Get Content Extraction by ID',
            endpoint: 'GET /content-extractions/{id}/',
        },
        {
            id: 'get-content-extractions-list',
            icon: <ArrowForwardIcon fontSize="small" />,
            title: 'List All Content Extractions',
            endpoint: 'GET/content-extractions/',
        },
        {
            id: 'post-content-extractions-restart',
            icon: <AddCircleIcon fontSize="small" />,
            title: 'Restart a Content Extraction by ID',
            endpoint: 'POST/content-extractions/{extraction_id}/restart/',
        },
        {
            id: 'get-api-health',
            icon: <HealthAndSafetyIcon fontSize="small" />,
            title: 'API Health Check',
            endpoint: 'GET /api/health/',
        },
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <Box sx={{ fontFamily: 'Inter, sans-serif', color: '#000000', display: 'flex', gap: 4 }}>
            {/* Sidebar */}
            <Box
                sx={{
                    width: 340,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'block' },
                }}
            >
                <Paper
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        top: 0,
                        maxHeight: 'calc(100vh - 40px)',
                        overflow: 'auto',
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: '#6a677e', fontWeight: 600, mb: 2, px: 1 }}>
                        API Listing: v1
                    </Typography>
                    {sidebarLinks.map((link) => (
                        <Box
                            key={link.id}
                            onClick={() => scrollToSection(link.id)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1,
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                '&:hover': { bgcolor: '#4c43a310' },
                            }}
                        >
                            <Box sx={{ color: primaryColor }}>{link.icon}</Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#6a677e', display: 'block' }}>
                                    {link.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                                    {link.endpoint}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Paper>
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ color: '#6a677e', mb: 2 }}>
                    API documentation for SCORM and API Integration modules.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Base URL:</strong>{' '}
                    <Typography component="code" sx={{ bgcolor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1, color: '#000' }}>
                        https://api.xtract2ai.com/v1/
                    </Typography>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    <strong style={{ display: 'block', marginBottom: '8px' }}>Authentication:</strong>
                    <strong>Header:</strong>{' '}
                    <Typography component="code" sx={{ bgcolor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1, color: '#000' }}>
                        X-API-KEY: &lt;your-api-key&gt;
                    </Typography>
                    <br />
                    <Typography variant="body2" sx={{ color: '#6a677e' }}>
                        (API keys must be generated by managers in the admin panel.)
                    </Typography>
                </Typography>

                {/* Info Cards */}
                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <KeyIcon sx={{ color: primaryColor }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                                API Key Management
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                            <strong>API Key Generation:</strong>
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    • API keys can only be generated by managers through the admin panel.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    • Each API key has an assigned owner.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    • Each user has an assigned Role.
                                </Typography>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <GroupIcon sx={{ color: primaryColor }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                                User Roles & Access Control
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                            <strong>User Roles:</strong>
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>Manager:</strong> Can access data at organization level (all files and
                                    extractions across the organization)
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>Member:</strong> Can only access their owned contents (files uploaded by them and
                                    associated extractions)
                                </Typography>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <WebhookIcon sx={{ color: primaryColor }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                                Webhook Management
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                            <strong>Webhook Creation:</strong> Webhooks can only be created by managers through the admin
                            panel. Each webhook has an assigned owner.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                            <strong>Configuration:</strong> Managers need to describe the endpoint URL and select the event
                            type for the webhook.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6a677e', mb: 1 }}>
                            <strong>Supported Events:</strong>
                        </Typography>
                        <List dense sx={{ py: 0, mb: 1 }}>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>content_extraction_completed:</strong> Triggers when content extraction is
                                    completed (both success and failure cases)
                                </Typography>
                            </ListItem>
                        </List>
                        <Typography variant="caption" sx={{ color: '#6a677e', mb: 2 }}>
                            The system will automatically send event details to the configured webhook endpoint when the
                            specified event occurs.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#000000' }}>
                            Sample Webhook Body
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, color: '#6a677e' }}>
                            <strong>Event:</strong>{' '}
                            <Typography
                                component="code"
                                sx={{ bgcolor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1, color: '#6a677e' }}
                            >
                                content_extraction_completed
                            </Typography>
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            <strong>Webhook Payload:</strong>
                        </Typography>
                        <CodeBlock
                            code={`{
  "content_extraction_id": 91,
  "scorm_file_id": 31,
  "batch_name": "",
  "download_url":"some_downloadable_url_link"
  "status": "completed",
  "finished_at": "2025-07-10T07:36:54.620631+00:00",
  "started_at": "2025-07-10T13:06:53.557708+00:00",
}`}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                            <strong>Payload Fields:</strong>
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>content_extraction_id:</strong> Unique identifier of the extraction process
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>scorm_file_id:</strong> ID of the associated SCORM file
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>status:</strong> Current status of the extraction (completed/failed)
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>started_at:</strong> When the extraction process started
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <Typography variant="body2" sx={{ color: '#6a677e' }}>
                                    <strong>finished_at:</strong> When the extraction completed (null if still in progress)
                                </Typography>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
                    Endpoints
                </Typography>

                <EndpointCard
                    id="post-scorm-files"
                    method="POST"
                    endpoint="/scorm-files/upload/"
                    description="Upload a SCORM file or register a public URL *Once uploaded, system will automatically start the content extraction process.*"
                    tag="Scorm"
                    requestExample={[
                        {
                            title: 'Request Example:',
                            content: `{
    "public_url": "https://cdn.example.com/files/your-file.zip",
    "batch_name": "example_batch_name"
}`,
                        },
                        {
                            title: 'Alternative Request Example (multipart/form-data):',
                            content: `Form fields:
  file: <the SCORM file itself>
  batch_name: example_batch_name`,
                        },
                        {
                            title: 'Example using cURL:',
                            content: `curl -X POST https://your-api.com/scorm-files/upload/ \
  -F "file=@/path/to/your-file.zip" \
  -F "batch_name=example_batch_name"`,
                        },
                    ]}
                    responseExample={`{
    "scorm_file_id": 38,
    "file_name": "file_name_in_text",
    "scorm_file": {
        "id": 38,
        "file_name": "file_name_in_text",
        "user": 1,
        "status": "processing",
        "storage_path": "storage_path_in_s3",
        "batch_name": "Lorem ipsuaasdasdasdasdasdasdasdasdasdaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        "file_size": null,
        "created_at": "2025-07-18T13:30:33.587545Z",
        "updated_at": "2025-07-18T13:30:33.604954Z",
        "content_extractions": [],
        "last_extraction": null,
        "public_url": "public_url_if_given",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remark": "File uploaded and validation started."
    },
    "message": "File is being processed and content extraction will start automatically."
}`}
                    importantParams={[
                        'scorm_file_id: Unique identifier for the uploaded SCORM package. This shall be used later to fetch extraction details and download the extracted contents.',
                        'file_name: Name of the uploaded SCORM file.',
                        'scorm_file: Object containing detailed information about the uploaded file.',
                        'scorm_file.status: Current processing status. Possible values:',
                        'processing: File has reached our server and is being prepared for content extraction.',
                        'processed: Preparation is done; file is ready for extraction.',
                        'failed:There were some issues processing the given file.',
                    ]}
                />

                <EndpointCard
                    id="get-scorm-file-id"
                    method="GET"
                    endpoint="/scorm-files/{scorm_file_id (retrieved from upload api)}/"
                    description="Fetch file object by id"
                    tag="Scorm"
                    responseExample={`{
    "id": 38,
    "file_name": "some_file_name",
    "user": 1,
    "status": "processed",
    "storage_path": "psi-pratham-softwares-pvt-ltd/scorm-files/38/some_file_name",
    "batch_name": "Lorem ipsuaasdasdasdasdasdasdasdasdasdaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    "file_size": 9.21,
    "created_at": "2025-07-18T13:30:33.587545Z",
    "updated_at": "2025-07-18T13:30:34.850737Z",
    "content_extractions": [
        {
            "id": 41,
            "status": "completed",
            "started_at": "2025-07-18T13:30:35.051222Z",
            "finished_at": "2025-07-18T13:30:36.711709Z",
            "file_id": 38,
            "final_output_path": "psi-pratham-softwares-pvt-ltd/scorm-files/38/content_extractions/41/some_file_name.json",
            "file_name": "some_file_name",
            "download_link": "some_downloadable_link_if_available",
            "is_deleted": false,
            "deleted_at": null,
            "deleted_by": null,
            "remarks": "Extraction completed."
        }
    ],
    "last_extraction": {
        "id": 41,
        "status": "completed",
        "started_at": "2025-07-18T13:30:35.051222Z",
        "finished_at": "2025-07-18T13:30:36.711709Z",
        "file_id": 38,
        "final_output_path": "psi-pratham-softwares-pvt-ltd/scorm-files/38/content_extractions/41/some_file_name.json",
        "file_name": "some_file_name",
        "download_link": "some_downloadable_link",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
    },
    "public_url": "public_url_if_given",
    "is_deleted": false,
    "deleted_at": null,
    "deleted_by": null,
    "remark": "File uploaded and processed successfully.",
    "client_ip": "182.156.218.70"
}`}
                    importantParams={[
                        'id: Unique identifier for the SCORM file.',
                        'file_name: Name of the SCORM file as uploaded.',
                        'user: User ID of the uploader.',
                        'status: Current processing status (see status explanations above).',
                        'file_size: Size of the file in MB.',
                        'created_at: Timestamp when the file was uploaded.',
                        'updated_at: Timestamp of the last update to this file record.',
                        'content_extractions: List of all extraction process objects associated with this file.',
                        'last_extraction: The most recent extraction process object associated with this file.',
                        'last_extraction.id: represents id of the extraction. Each extraction process has a unique identifier, which can be used to restart, fetch and other APIs.',
                        'public_url: Publicly accessible URL for the file, if available.',
                    ]}
                />

                <EndpointCard
                    id="delete-scorm-file-id"
                    method="DELETE"
                    endpoint="/scorm-files/{scorm_file_id}/"
                    description="Delete a SCORM file by its ID."
                    tag="Scorm"
                    responseExample={`{
  "message": "SCORM file with ID 70 deleted successfully."
}`}
                    importantParams={['scorm_file_id: The unique identifier of the SCORM file to delete']}
                />

                <EndpointCard
                    id="get-scorm-files"
                    method="GET"
                    endpoint="/scorm-files/"
                    description="Returns a list of SCORM files with pagination. Every file object will only show last_extraction object."
                    tag="Scorm"
                    responseExample={`{
"count": 15,
"next": "http://127.0.0.1:8000/api/v1/scorm-files/?page=2",
"previous": null,
"results": [
{
    "id": 81,
    "file_name": "some_file_name",
    "user": 3,
    "status": "processed",
    "storage_path": "tech-solutions-inc/scorm-files/81/some_file_name",
    "batch_name": null,
    "file_size": 5.36,   
    "created_at": "2025-07-14T12:38:59.532811Z",
    "updated_at": "2025-07-14T12:39:15.150704Z",
    "last_extraction": {
        "id": 113,
        "status": "dispatched",
        "started_at": "2025-07-14T12:39:16.971930Z",
        "finished_at": null,
        "file_id": 81,
        "final_output_path": null,
        "file_name": "some_file_name",
        "download_link": null,
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": null
    },
    "public_url": null,
    "is_deleted": false,
    "deleted_at": null,
    "deleted_by": null,
    "api_key_name": "My Api Key",
    "remark": "File uploaded and processed successfully."
},
{
    "id": 71,
    "file_name": "some-scorm-package-name-example.zip",
    "user": 6,
    "status": "processed",
    "extraction": null,
    "file_size": 5.43,
    "created_at": "2025-07-10T19:34:24.304996Z",
    "updated_at": "2025-07-10T19:34:34.212750Z",
    "last_extraction": {
        "id": 95,
        "status": "dispatched",
        "started_at": "2025-07-10T19:34:34.205419Z",
        "finished_at": null,
        "file_id": 71,
        "final_output_path": null,
        "file_name": "some-scorm-package-name-example.zip",
        "download_link": null
    },
    "public_url": null
},
{
    "id": 70,
    "file_name": "some-scorm-file-name-example.zip",
    "user": 6,
    "status": "extraction_stalled",
    "extraction": null,
    "file_size": 12.81,
    "created_at": "2025-07-09T22:18:17.692487Z",
    "updated_at": "2025-07-09T22:18:36.539755Z",
    "last_extraction": {
        "id": 94,
        "status": "dispatched",
        "started_at": "2025-07-09T22:18:36.532752Z",
        "finished_at": null,
        "file_id": 70,
        "final_output_path": null,
        "file_name": "some-scorm-file-name-example.zip",
        "download_link": null
    },
    "public_url": "https://cdn.example.com/files/your-file.zip"
},
{
    "id": 69,
    "file_name": "some-scorm-file-name-example.zip",
    "user": 6,
    "status": "failed",
    "extraction": null,
    "file_size": 12.81,
    "created_at": "2025-07-09T22:13:24.210582Z",
    "updated_at": "2025-07-09T22:13:49.462508Z",
    "last_extraction": {
        "id": 93,
        "status": "pending",
        "started_at": null,
        "finished_at": null,
        "file_id": 69,
        "final_output_path": null,
        "file_name": "some-scorm-file-name-example.zip",
        "download_link": null
    },
    "public_url": "https://cdn.example.com/files/your-file.zip"
},
{
    "id": 68,
    "file_name": "some-scorm-package-name-example.zip",
    "user": 6,
    "status": "extraction_stalled",
    "extraction": null,
    "file_size": 5.43,
    "created_at": "2025-07-09T22:12:27.266552Z",
    "updated_at": "2025-07-09T22:12:35.308332Z",
    "last_extraction": {
        "id": 92,
        "status": "dispatched",
        "started_at": "2025-07-09T22:12:35.301237Z",
        "finished_at": null,
        "file_id": 68,
        "final_output_path": null,
        "file_name": "some-scorm-package-name-example.zip",
        "download_link": null
    },
    "public_url": null
},
{
    "id": 66,
    "file_name": "some-scorm-file-name-example.zip",
    "user": 6,
    "status": "content_extraction_completed",
    "extraction": null,
    "file_size": 43.59,
    "created_at": "2025-07-04T13:25:31.293405Z",
    "updated_at": "2025-07-04T13:27:22.710979Z",
    "last_extraction": {
        "id": 86,
        "status": "completed",
        "started_at": "2025-07-04T18:57:29.491990Z",
        "finished_at": "2025-07-04T19:02:13.112184Z",
        "file_id": 66,
        "final_output_path": "content_extractions/86/some-scorm-file-name-example.zip.json",
        "file_name": "some-scorm-file-name-example.zip",
        "download_link": "https://cdn.example.com/content_extractions/86/some-scorm-file-name-example.zip.json"
    },
    "public_url": null
}
]
}`}
                    importantParams={[
                        'count: Total number of SCORM files available.',
                        'next: URL to the next page of results (if paginated).',
                        'previous: URL to the previous page of results (if paginated).',
                        'results: List of SCORM file objects, each containing:',
                        'id: Unique identifier for the SCORM file.',
                        'file_name: Name of the SCORM file.',
                        'status: Processing status (e.g., completed, in progress, failed).',
                        'file_size: Size of the file in MB.',
                        'created_at: Upload timestamp.',
                        'updated_at: Last update timestamp.',
                        'last_extraction: Details about the most recent extraction process.',
                        'public_url: Publicly accessible URL for the file (if available).',
                    ]}
                />

                <EndpointCard
                    id="post-content-extractions"
                    method="POST"
                    endpoint="/content-extractions/"
                    description="Start content extraction for a SCORM file."
                    tag="Scorm"
                    requestExample={`{
  "file_id": 67
}`}
                    responseExample={`{
  "extraction_id": 97,
  "status": "dispatched",
  "started_at": "2025-07-10T20:31:28.833394Z",
  "scorm_file_id": 67,
  "scorm_file": {
    "id": 67,
    "file_name": "a-scorm-file-name-example.zip",
    "user": 3,
    "status": "processed",
    "extraction": null,
    "file_size": 5.39,
    "created_at": "2025-07-08T11:25:40.013225Z",
    "updated_at": "2025-07-10T20:31:28.842398Z",
    "content_extractions": [
      {
        "id": 87,
        "status": "dispatched",
        "started_at": "2025-07-10T07:17:54.854926Z",
        "finished_at": null,
        "file_id": 67,
        "final_output_path": null,
        "file_name": "a-scorm-file-name-example.zip",
        "download_link": null
      },
      {
        "id": 96,
        "status": "dispatched",
        "started_at": "2025-07-10T20:30:27.165909Z",
        "finished_at": null,
        "file_id": 67,
        "final_output_path": null,
        "file_name": "a-scorm-file-name-example.zip",
        "download_link": null
      },
      {
        "id": 97,
        "status": "dispatched",
        "started_at": "2025-07-10T20:31:28.833394Z",
        "finished_at": null,
        "file_id": 67,
        "final_output_path": null,
        "file_name": "a-scorm-file-name-example.zip",
        "download_link": null
      }
    ],
    "last_extraction": {
      "id": 97,
      "status": "dispatched",
      "started_at": "2025-07-10T20:31:28.833394Z",
      "finished_at": null,
      "file_id": 67,
      "final_output_path": null,
      "file_name": "a-scorm-file-name-example.zip",
      "download_link": null
    },
                            "public_url": null
                            },
  "sqs_message_id": "8b9ee33b-6bec-4532-afa9-ac1cb1f00cbf"
}`}
                    importantParams={[
                        'extraction_id: Unique identifier for the extraction process.',
                        'status: Status of the extraction process. Possible values',
                        'pending: Extraction process has just started and is waiting to be dispatched.',
                        'dispatched: Job is sent for extraction and is queued for processing.',
                        'in_progress: Extraction job has started and is being processed by the extractor.',
                        'failed: Extraction process failed due to an error.',
                        'success: Extraction process completed successfully.',
                        'started_at: Timestamp when the extraction process started.',
                        'scorm_file_id: The ID of the SCORM file for which extraction was started.',
                        'scorm_file: The full SCORM file object, including all extractions and last_extraction.',
                        'sqs_message_id: Internal message queue ID for tracking the extraction job.',
                    ]}
                />

                <EndpointCard
                    id="get-content-extractions-id"
                    method="GET"
                    endpoint="/content-extractions/{extraction_id (can be found in scorm-files apis)}/"
                    description="Content Extraction Details"
                    responseExample={`{
    "id": 39,
    "status": "dispatched",
    "started_at": "2025-07-18T14:05:04.196554Z",
    "finished_at": null,
    "file_id": 36,
    "final_output_path": null,
    "file_name": ".zip",
    "download_link": null,
    "is_deleted": false,
    "deleted_at": null,
    "deleted_by": null,
    "remarks": "Extraction completed."
}`}
                    importantParams={[
                        'id: Unique identifier for the extraction process.',
                        'status: Status of the extraction process (see status explanations above).',
                        'started_at: Timestamp when the extraction process started.',
                        'finished_at: Timestamp when the extraction process finished (if completed).',
                        'file_id: The ID of the SCORM file this extraction belongs to.',
                        'final_output_path: Path to the extracted output file (relative to storage).',
                        'file_name: Name of the original SCORM file.',
                        'download_link: Direct link to download the extracted output (if available).',
                    ]}
                />

                <EndpointCard
                    id="get-content-extractions-list"
                    method="GET"
                    endpoint="/content-extractions/"
                    description="List Content Extractions"
                    responseExample={`{
    "count": 41,
    "next": "?page=2",
    "previous": null,
    "results": [
        {
        "id": 41,
        "status": "completed",
        "started_at": "2025-07-18T13:30:35.051222Z",
        "finished_at": "2025-07-18T13:30:36.711709Z",
        "file_id": 38,
        "final_output_path": "/file_41.json",
        "file_name": ".zip",
        "download_link": "?signature=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        },
        {
        "id": 40,
        "status": "completed",
        "started_at": "2025-07-18T13:25:20.803125Z",
        "finished_at": "2025-07-18T13:25:22.434268Z",
        "file_id": 37,
        "final_output_path": "/file_40.json",
        "file_name": ".zip",
        "download_link": "?signature=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        },
        {
        "id": 39,
        "status": "completed",
        "started_at": "2025-07-18T12:47:15.883288Z",
        "finished_at": "2025-07-18T12:47:17.516729Z",
        "file_id": 36,
        "final_output_path": "/file_39.json",
        "file_name": ".zip",
        "download_link": "?signature=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        },
        {
        "id": 38,
        "status": "completed",
        "started_at": "2025-07-18T12:46:14.241019Z",
        "finished_at": "2025-07-18T12:46:15.865113Z",
        "file_id": 35,
        "final_output_path": "/file_38.json",
        "file_name": ".zip",
        "download_link": "?signature=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        },
        {
        "id": 37,
        "status": "completed",
        "started_at": "2025-07-17T11:41:51.653675Z",
        "finished_at": "2025-07-17T11:42:03.296077Z",
        "file_id": 34,
        "final_output_path": "/file_37.json",
        "file_name": ".zip",
        "download_link": "?signature=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        }
    ]
    }`}
                    importantParams={[
                        'count: Total number of content extraction objects available.',
                        'next: URL to the next page of results (if paginated).',
                        'previous: URL to the previous page of results (if paginated).',
                        'results: List of content_extraction objects (see above for object details).',
                    ]}
                />

                <EndpointCard
                    id="post-content-extractions-restart"
                    method="POST"
                    endpoint="/content-extractions/{extraction_id}/restart/"
                    description="Restart a content extraction process by its ID."
                    tag="Scorm"
                    responseExample={`{
    "message": "Extraction restarted",
    "extraction_id": 39,
    "status": "dispatched",
    "started_at": "2025-07-18T14:05:04.196554Z",
    "scorm_file_id": 36,
    "scorm_file": {
        "id": 36,
        "file_name": ".zip",
        "user": 1,
        "status": "processed",
        "storage_path": "/36/.zip",
        "batch_name": null,
        "file_size": 9.21,
        "created_at": "2025-07-18T12:47:15.047588Z",
        "updated_at": "2025-07-18T12:47:15.747224Z",
        "content_extractions": [
        {
            "id": 39,
            "status": "dispatched",
            "started_at": "2025-07-18T14:05:04.196554Z",
            "finished_at": null,
            "file_id": 36,
            "final_output_path": null,
            "file_name": ".zip",
            "download_link": null,
            "is_deleted": false,
            "deleted_at": null,
            "deleted_by": null,
            "remarks": "Extraction completed."
        }
        ],
        "last_extraction": {
        "id": 39,
        "status": "dispatched",
        "started_at": "2025-07-18T14:05:04.196554Z",
        "finished_at": null,
        "file_id": 36,
        "final_output_path": null,
        "file_name": ".zip",
        "download_link": null,
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remarks": "Extraction completed."
        },
        "public_url": "?token=***",
        "is_deleted": false,
        "deleted_at": null,
        "deleted_by": null,
        "remark": "File uploaded and processed successfully."
    },
    "sqs_message_id": ""
    }`}
                    importantParams={[
                        'message: Confirmation message indicating the extraction was restarted.',
                        'extraction_id: The ID of the restarted extraction process.',
                        'status: Status of the restarted extraction (typically "dispatched").',
                        'started_at: Timestamp when the restarted extraction process began.',
                        'scorm_file_id: The ID of the SCORM file associated with this extraction.',
                        'scorm_file: The full SCORM file object with updated extraction information.',
                        'sqs_message_id: Internal message queue ID for tracking the restarted extraction job.',
                    ]}
                />

                <EndpointCard
                    id="get-api-health"
                    method="GET"
                    endpoint="https://api.xtract2ai.com/health/"
                    description="Health check endpoint to verify API is up and running."
                    tag="Utility"
                    responseExample={`{
  "status": "healthy",
  "version": "v1",
  "timestamp": "2025-07-14T19:03:50.711280",
  "service": "PSI SCORM Lens API"
}`}
                    importantParams={[
                        'status: Returns healthy if the API is healthy and reachable.',
                        'version: The current version of the API.',
                        'timestamp: The server time when the health check was performed.',
                        'service: The name of the API service.',
                    ]}
                />
            </Box>
        </Box>
    );
};

export default ApiDocumentation;
